import { DateOnly, differenceInDays } from '@if/core/date'
import { formatDate } from '@if/core/utils'
import { getToday, getTimeNow } from '@src/services/DateTimeProvider'
import { ELoanStatus, EOperationStatus } from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'

export const useLoanCalendarCurrentLoanStatus = (simulation: CalendarSimulation): ELoanStatus | null => {
  return useMemo(() => simulation.getCurrentLoanStatus(), [simulation])
}

export const useLoanSummaryInformation = (simulation: CalendarSimulation) => {
  return useMemo(() => {
    return simulation.summaryInformation || null
  }, [simulation])
}

export const useGetCurrentOverdueAmount = (simulation: CalendarSimulation) => {
  return useLoanSummaryInformation(simulation)?.pastdueAndAccruedInterest || 0
}

export const useGetOutstandingBalance = (simulation: CalendarSimulation) => {
  return useLoanSummaryInformation(simulation)?.outstandingBalance || 0
}

export const useLoanCalendarNbOfCurrentLateDays = (simulation: CalendarSimulation) => {
  return useLoanSummaryInformation(simulation)?.daysLate || 0
}

export const useLoanCalendarNearestOperationInformation = (simulation: CalendarSimulation) => {
  return useMemo(() => simulation.getNearestOperationInformation(), [simulation])
}

export const useLoanCurrentBalance = (simulation: CalendarSimulation) => {
  const nearestOperationInfo = useLoanCalendarNearestOperationInformation(simulation)
  return useMemo(
    () => nearestOperationInfo?.operationResultDto.loanBalance || simulation.getTotalFundedProductsAmount(),
    [simulation, nearestOperationInfo?.operationResultDto.loanBalance],
  )
}

export const useLoanCalendarLastCompletedPaymentDate = (simulation: CalendarSimulation): DateOnly | null => {
  return useMemo(() => {
    const today = getTimeNow()

    if (today <= simulation.fullLoan.loan.activationOn) return null

    for (let i = simulation.operationInformations.length - 1; i >= 0; i--) {
      const operationInformation = simulation.operationInformations[i]
      if (
        operationInformation.operation.operationStatusId === EOperationStatus.Complete &&
        operationInformation.isCustomerPaymentOperation()
      ) {
        return operationInformation.operation.effectiveDate
      }
    }
    return null
  }, [simulation.fullLoan.loan.activationOn, simulation.operationInformations])
}

export const useLoanCalendarNbDaysSinceLastCompletedPayment = (simulation: CalendarSimulation): number | null => {
  const lastPaymentDate: DateOnly | null = useLoanCalendarLastCompletedPaymentDate(simulation)
  if (!lastPaymentDate) {
    return null
  }
  const today = getToday()
  return Math.max(0, differenceInDays(formatDate(today), lastPaymentDate))
}

export const useLoanCalendarActivationOn = (simulation: CalendarSimulation) => {
  return useMemo(() => simulation.fullLoan.loan.activationOn, [simulation])
}

export const useLoanCalendarMetro2Reportings = (simulation: CalendarSimulation) => {
  return useMemo(() => simulation.fullLoan.metro2Reportings, [simulation])
}
